import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PersonIcon, FileIcon, ClockIcon, DeleteIcon, InfoIcon, ViewIcon, PlayIcon, SearchIcon,
  ChevronRightIcon, AlertTriangleIcon, CheckIcon, XSmallIcon, UploadIcon,
} from '@shopify/polaris-icons';
import { api } from './apiClient';
import { useAlerts } from '../alerts';
import { Ic, StatusPill, EmDash } from '../ui';
import SystemsTable from './SystemsTable';
import {
  runState, stateOf, pillTone, systemLabel, formatDateTime, isRealRun, runPath,
} from './status';

const CONFIRM_PHRASE = 'OFFBOARD';
const DELETE_CONFIRM_PHRASE = 'DELETE';
const ACTIVE_TRANSFER_STATUSES = ['pending', 'inprogress'];
const TABS = ['single', 'csv', 'history', 'delete'];
const RECENT_ROWS = 8;
const STATS_WINDOW_DAYS = 30;
// list_unified_history's default page size — stats past this are a floor.
const HISTORY_LIMIT = 50;

// History status filter. 'attention' is what the old "Needs attention"
// checkbox meant: waiting on a person, or failed.
const FILTERS = [
  ['all', 'All statuses'],
  ['attention', 'Needs attention or failed'],
  ['ful', 'Complete'],
  ['par', 'Running or scheduled'],
  ['hold', 'Needs attention'],
  ['can', 'Failed'],
  ['none', 'No account found'],
  ['skip', 'Skipped or cancelled'],
];
const matchesFilter = (run, filter) => {
  if (filter === 'all') return true;
  const tone = runState(run)[0];
  return filter === 'attention' ? tone === 'hold' || tone === 'can' : tone === filter;
};

function splitAddresses(raw) {
  return raw.split(/[,;]/).map(s => s.trim()).filter(Boolean);
}

// Phase 1 only *starts* the GYB backup in the background and doesn't wait
// on it or verify it (that's Phase 2's job, not built yet) — the only
// way to check progress right now is this log file on the server.
function gybLogHint(rows) {
  const paths = (rows || []).map(r => {
    const localPart = r.email.split('@')[0];
    return `tail -f /opt/soccer-post-utility-hub/data/offboarding/logs/gyb-backup-${localPart}.log`;
  });
  return paths.join('\n');
}

function Alert({ tone = '', children }) {
  const icon = tone === 'ok' ? CheckIcon : tone === 'info' ? InfoIcon : AlertTriangleIcon;
  return <div className={`uh-alert ${tone}`}><Ic source={icon} sm /><div>{children}</div></div>;
}

// Workspace preview — what Phase 1 will do to each Google account.
function PreviewTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="uh-tscroll">
      <table className="uh-tbl">
        <thead>
          <tr>
            <th>Name / email</th><th>Status</th><th>Renamed to</th><th>Continuity group</th>
            <th>Subject prefix</th><th>Forwards to</th><th>Moves to OU</th><th>Mail backup (local host only)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.email}-${r.full_name}-${i}`}>
              <td className="nm">{r.email || r.full_name || '(no name or email)'}</td>
              <td>
                {r.already_offboarded
                  ? <StatusPill tone="hold">Already offboarded</StatusPill>
                  : r.exists
                    ? <StatusPill tone="ful">Found</StatusPill>
                    : <StatusPill tone={r.email ? 'can' : 'neu'}>{r.error || (r.email ? 'Not found' : 'Searched by name at run time')}</StatusPill>}
              </td>
              {/* archived_email === email means no rename will happen (no forward-to given). */}
              <td className="uh-mono">
                {!r.archived_email ? <EmDash /> : r.archived_email !== r.email ? r.archived_email : <span className="uh-muted">No rename (no forward-to)</span>}
              </td>
              <td className="uh-mono">{r.group_email || <EmDash />}</td>
              <td>{r.subject_prefix || <EmDash />}</td>
              <td className="uh-mono">
                {r.forward_to.length ? r.forward_to.join(', ') : <span className="uh-muted">None. Suspended and archived only</span>}
              </td>
              <td>{r.target_ou || <EmDash />}</td>
              <td>{r.backup_target || <EmDash />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeletionStatus({ status }) {
  if (status === 'completed') return <span className="uh-prog done"><span className="bar"><i style={{ width: '100%' }} /></span>Transferred</span>;
  if (status === 'failed') return <StatusPill tone="can">Transfer failed</StatusPill>;
  if (status === 'not_started') return <StatusPill tone="neu">Not started</StatusPill>;
  if (ACTIVE_TRANSFER_STATUSES.includes(status)) {
    return <span className="uh-prog run"><span className="bar"><i /></span>Transferring · checking every few seconds</span>;
  }
  return <StatusPill tone="par">{status}</StatusPill>;
}

// One row of History: who, every system's state, who started it, when.
function HistoryRow({ run, onOpen }) {
  const { nameOf } = useAuth();
  const [tone, label] = runState(run);
  const systems = run.systems || [];
  const tones = systems.map((s) => stateOf(s.status, s.outcome)[0]);
  const relevant = tones.filter((t) => t !== 'none' && t !== 'skip').length;
  const done = tones.filter((t) => t === 'ful').length;
  return (
    <tr className="click" tabIndex={0} onClick={() => onOpen(run)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(run); }}>
      <td>
        <div className="nm">{run.full_name || run.email}</div>
        <div className="sm">
          {!run.full_name ? 'Google Workspace only (CSV import)' : run.email ? <span className="uh-mono">{run.email}</span> : 'No company email'}
        </div>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className="uh-pips">
          {systems.map((s, i) => (
            <i key={s.system} className={`uh-pip ${tones[i] === 'neu' ? 'skip' : tones[i]}`}
               title={`${systemLabel(s.system)}: ${stateOf(s.status, s.outcome)[1]}`} />
          ))}
        </span>
        <span className="uh-pipcount">{relevant ? `${done}/${relevant}` : '—'}</span>
      </td>
      <td>{run.initiated_by ? nameOf(run.initiated_by) : <span className="uh-muted">CSV import</span>}</td>
      <td className="uh-num" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(run.created_at)}</td>
      <td><StatusPill tone={pillTone(tone)}>{label}</StatusPill></td>
      <td className="go"><Ic source={ChevronRightIcon} sm /></td>
    </tr>
  );
}

function HistoryTable({ rows, loading, error, limit, onOpen, onClear, filtered }) {
  const shown = limit ? rows.slice(0, limit) : rows;
  return (
    <div className="uh-tscroll">
      <table className="uh-tbl">
        <thead><tr><th>Name</th><th>Systems</th><th>Started by</th><th>Started at</th><th>Status</th><th /></tr></thead>
        <tbody>
          {error ? (
            <tr><td colSpan={6}><Alert tone="crit">{error}</Alert></td></tr>
          ) : loading && !rows.length ? (
            <tr><td colSpan={6} className="uh-empty">Loading…</td></tr>
          ) : !shown.length ? (
            <tr><td colSpan={6} className="uh-empty">
              {filtered
                ? <>No runs match. <button type="button" className="uh-link" onClick={onClear}>Clear filters</button></>
                : 'No one has been offboarded through this tool yet.'}
            </td></tr>
          ) : shown.map((run) => <HistoryRow key={run.id} run={run} onOpen={onOpen} />)}
        </tbody>
      </table>
    </div>
  );
}

const Legend = () => (
  <span className="uh-legend">
    <span><i className="uh-pip ful" />Done</span>
    <span><i className="uh-pip par" />Running / scheduled</span>
    <span><i className="uh-pip hold" />Needs you</span>
    <span><i className="uh-pip can" />Failed</span>
    <span><i className="uh-pip none" />No account</span>
  </span>
);

function Offboarding() {
  const { refresh: refreshAlerts } = useAlerts();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'single';
  const setTab = (next) => setParams(next === 'single' ? {} : { tab: next });


  // Single-user form
  const [email, setEmail] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [notifyTo, setNotifyTo] = useState('');
  const [fullName, setFullName] = useState('');
  const [scheduledFor, setScheduledFor] = useState(''); // datetime-local input value, '' = run now

  // Multi-system run (Workspace + Shopify/Lightspeed/Team Portal/...) —
  // single-user tab, see full_run.py.
  const [fullRun, setFullRun] = useState(null);
  const [runId, setRunId] = useState(null);
  const [fullRunError, setFullRunError] = useState('');
  const [resolveBusyActionId, setResolveBusyActionId] = useState(null);

  // CSV import
  const [csvFile, setCsvFile] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  const csvInputRef = useRef(null);

  // Shared preview/run state
  const [previewRows, setPreviewRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]); // the DepartureRow payload behind the preview
  const [previewSource, setPreviewSource] = useState(null); // 'single' | 'csv' — which tab the preview belongs to
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewRef = useRef(null);

  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [runLog, setRunLog] = useState([]);
  const [runResult, setRunResult] = useState(null); // {exit_code}
  const [csvBatchResult, setCsvBatchResult] = useState(null); // {run_ids} — CSV multi-system batch
  const runAbortRef = useRef(null);
  const logRef = useRef(null);

  // History — one merged list: real multi-system runs and CSV-only
  // (Workspace-only) departures together, see full_run.list_unified_history().
  // allRuns is the unfiltered list (stats + recent runs); searchRuns is the
  // server-side search result while a search is typed.
  const [allRuns, setAllRuns] = useState([]);
  const [searchRuns, setSearchRuns] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete (final step — Drive transfer + permanent removal)
  const [deletionRows, setDeletionRows] = useState([]);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionError, setDeletionError] = useState('');
  const [transferBusy, setTransferBusy] = useState({}); // archived_email -> bool
  const [deleteBusy, setDeleteBusy] = useState({}); // archived_email -> bool
  const [rowErrors, setRowErrors] = useState({}); // archived_email -> string
  const [deleteTarget, setDeleteTarget] = useState(null); // row being confirmed in the dialog
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteDialogRef = useRef(null);

  useEffect(() => {
    loadHistory();
    loadDeletionCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'delete') loadDeletionCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [runLog]);

  // Server-side search, debounced — it also reaches runs past the first page.
  useEffect(() => {
    const q = historySearch.trim();
    if (!q) { setSearchRuns(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const data = await api.fullRunHistory(q, false);
        setSearchRuns(data.runs || []);
      } catch (e) {
        setHistoryError(e.message || String(e));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [historySearch]);

  // Poll the active single-user run while anything in it is still
  // in_progress (Workspace's archive step usually — Phase 2's sweep can
  // take hours for a large mailbox) — same interval pattern as the
  // Delete tab's transfer-status polling.
  useEffect(() => {
    if (!runId || !fullRun) return;
    const hasActive = fullRun.accounts.some(a => a.status === 'in_progress');
    if (!hasActive) return;
    const interval = setInterval(async () => {
      try {
        const updated = await api.getFullRun(runId);
        setFullRun(updated);
      } catch {
        // transient — next tick retries
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, fullRun]);

  // Poll live transfer status while any row is pending/inprogress — a real
  // transfer with real files can take a while, and status must always come
  // from Google at delete-time anyway, so polling here is just for the UI.
  useEffect(() => {
    if (tab !== 'delete') return;
    const active = deletionRows.filter(r => ACTIVE_TRANSFER_STATUSES.includes(r.transfer_status));
    if (active.length === 0) return;
    const interval = setInterval(async () => {
      for (const row of active) {
        try {
          const data = await api.transferStatus(row.archived_email);
          setDeletionRows(prev => prev.map(r =>
            r.archived_email === row.archived_email ? { ...r, transfer_status: data.transfer_status } : r
          ));
        } catch {
          // transient — next tick will retry
        }
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, deletionRows]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const data = await api.fullRunHistory('', false);
      setAllRuns(data.runs || []);
      setServerSystems(data.systems || []);
      setNotifyAlways(data.notify_always || []);
    } catch (e) {
      setHistoryError(e.message || String(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadDeletionCandidates = async () => {
    setDeletionLoading(true);
    setDeletionError('');
    try {
      const data = await api.deletionCandidates();
      setDeletionRows(data.rows || []);
    } catch (e) {
      setDeletionError(e.message || String(e));
    } finally {
      setDeletionLoading(false);
    }
  };

  const startTransfer = async (archivedEmail) => {
    setTransferBusy(prev => ({ ...prev, [archivedEmail]: true }));
    setRowErrors(prev => ({ ...prev, [archivedEmail]: '' }));
    try {
      const data = await api.startTransfer(archivedEmail);
      setDeletionRows(prev => prev.map(r =>
        r.archived_email === archivedEmail
          ? { ...r, transfer_status: data.transfer_status, transfer_id: data.transfer_id }
          : r
      ));
    } catch (e) {
      setRowErrors(prev => ({ ...prev, [archivedEmail]: e.message || String(e) }));
    } finally {
      setTransferBusy(prev => ({ ...prev, [archivedEmail]: false }));
    }
  };

  const deleteAccount = async (archivedEmail, phrase) => {
    setDeleteBusy(prev => ({ ...prev, [archivedEmail]: true }));
    setRowErrors(prev => ({ ...prev, [archivedEmail]: '' }));
    try {
      await api.deleteAccount(archivedEmail, phrase);
      setDeletionRows(prev => prev.filter(r => r.archived_email !== archivedEmail));
      refreshAlerts();
    } catch (e) {
      setRowErrors(prev => ({ ...prev, [archivedEmail]: e.message || String(e) }));
    } finally {
      setDeleteBusy(prev => ({ ...prev, [archivedEmail]: false }));
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteConfirmText('');
    const dlg = deleteDialogRef.current;
    if (dlg?.showModal) dlg.showModal(); else dlg?.setAttribute('open', '');
  };
  const closeDeleteDialog = () => {
    deleteDialogRef.current?.close?.();
    setDeleteTarget(null);
  };
  const confirmDelete = () => {
    const target = deleteTarget;
    const phrase = deleteConfirmText;
    closeDeleteDialog();
    if (target) deleteAccount(target.archived_email, phrase);
  };

  const resetRunState = () => {
    setConfirmText('');
    setRunning(false);
    setRunLog([]);
    setRunResult(null);
    setFullRun(null);
    setRunId(null);
    setFullRunError('');
    setCsvBatchResult(null);
  };

  const showPreview = () => setTimeout(() => previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);

  const previewSingleUser = async () => {
    setPreviewError('');
    setPreviewLoading(true);
    resetRunState();
    try {
      const rows = [{ email: email.trim(), full_name: fullName.trim(), forward_to: splitAddresses(forwardTo) }];
      const data = await api.preview(rows);
      setPreviewRows(data.rows);
      setPendingRows(rows);
      setPreviewSource('single');
      showPreview();
    } catch (e) {
      setPreviewError(e.message || String(e));
      setPreviewRows([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const previewCsv = async () => {
    if (!csvFile) return;
    setPreviewError('');
    setCsvErrors([]);
    setPreviewLoading(true);
    resetRunState();
    try {
      const data = await api.csvPreview(csvFile);
      setPreviewRows(data.rows);
      setCsvErrors(data.parse_errors || []);
      setPendingRows(
        data.rows.map(r => ({ email: r.email, full_name: r.full_name, forward_to: r.forward_to }))
      );
      setPreviewSource('csv');
      showPreview();
    } catch (e) {
      setPreviewError(e.message || String(e));
      setPreviewRows([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  // exists/already_offboarded only apply when a row actually has an
  // email — a name-only row (no company email, see preview.py's
  // blank-email branch) always previews as exists=false, which is
  // correct and expected, not a reason to block the run.
  const rowIsReadyToRun = (r) => (r.email ? (r.exists && !r.already_offboarded) : true);

  const previewVisible = previewRows.length > 0 && previewSource === tab;
  const canRun =
    previewVisible &&
    pendingRows.length > 0 &&
    previewRows.every(rowIsReadyToRun) &&
    confirmText === CONFIRM_PHRASE &&
    !running &&
    (tab !== 'single' || fullName.trim().length > 0);

  // CSV import's multi-system entrypoint (2026-09-23) — one synchronous
  // batch POST, not the old Phase-1-only SSE stream. Every registered
  // connector runs for every row; jumps to History filtered to "needs
  // attention" afterward, the natural place to see anything a name-only
  // row's Workspace name-search flagged or any connector fuzzy match.
  const startCsvBatchRun = async () => {
    setRunning(true);
    setCsvBatchResult(null);
    setFullRunError('');
    try {
      const result = await api.runFullCsvBatch(pendingRows, confirmText);
      setCsvBatchResult(result);
      setStatusFilter('attention');
      setTab('history');
      loadHistory();
      refreshAlerts();
    } catch (e) {
      setFullRunError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  // Single user — starts the multi-system run (creates tracking rows,
  // executes Shopify/Lightspeed/Team Portal synchronously), then drives
  // Phase 1's existing SSE /run endpoint for Workspace exactly as before
  // (keeping its live log), and reports completion back onto the run once
  // that SSE stream finishes.
  const startSingleUserFullRun = async () => {
    setRunning(true);
    setRunLog([]);
    setRunResult(null);
    setFullRun(null);
    setFullRunError('');

    // datetime-local gives a naive "YYYY-MM-DDTHH:mm" in the browser's
    // local timezone — new Date(...) parses that as local time, and
    // toISOString() converts it to UTC for the API (which requires an
    // aware datetime in the future — see FullRunRequest._check_scheduled_for).
    const scheduledForIso = scheduledFor ? new Date(scheduledFor).toISOString() : null;

    let startedRunId;
    try {
      const initialTree = await api.startFullRun(fullName.trim(), email.trim(), forwardTo, confirmText, scheduledForIso, notifyTo.trim());
      startedRunId = initialTree.id;
      setRunId(startedRunId);
      setFullRun(initialTree);
      refreshAlerts();
    } catch (e) {
      // "409 Conflict: Jane is already being offboarded (run #76 ...)" reads better without the status prefix.
      setFullRunError((e.message || String(e)).replace(/^\d{3} [A-Za-z ]+: /, ''));
      setRunning(false);
      return;
    }

    if (scheduledForIso || !email.trim()) {
      // Scheduled: nothing to drive live — Workspace isn't touched until
      // the scheduled time (offboarding.scheduled_runs, cron).
      // No email: Workspace was already handled inside start_run()'s
      // name-search branch (flagged for approval, or a plain "No Account
      // Found"), not via the SSE endpoint, which requires one and would
      // 400 otherwise.
      setRunning(false);
      loadHistory();
      return;
    }

    // Plain local var, not runLog state — reading state inside onDone's
    // closure would capture it as it was when api.run() was called (empty),
    // not the accumulated value. This is what lets the backend see a
    // NEEDS_REVIEW marker from Phase 1's script output (see
    // offboarding/workspace.py's phase1_result()) instead of always
    // getting an empty detail string.
    const collectedLog = [];
    runAbortRef.current = api.run(
      pendingRows,
      confirmText,
      (evt) => {
        if (evt.line !== undefined) {
          collectedLog.push(evt.line);
          setRunLog(prev => [...prev, evt.line]);
        }
      },
      async (result) => {
        setRunning(false);
        setRunResult(result);
        loadHistory();
        try {
          const updated = await api.reportWorkspaceComplete(startedRunId, result.exit_code, collectedLog.join('\n'));
          setFullRun(updated);
          refreshAlerts();
        } catch (e) {
          setFullRunError(e.message || String(e));
        }
      },
    );
  };

  const handleResolveAction = async (actionId, decision, candidateId) => {
    if (!runId) return;
    setResolveBusyActionId(actionId);
    try {
      const updated = await api.resolveFullRunAction(runId, actionId, decision, candidateId);
      setFullRun(updated);
      refreshAlerts();
    } catch (e) {
      setFullRunError(e.message || String(e));
    } finally {
      setResolveBusyActionId(null);
    }
  };

  const cancelRun = () => {
    runAbortRef.current?.abort();
    setRunning(false);
  };

  // ── derived ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const since = Date.now() - STATS_WINDOW_DAYS * 24 * 3600 * 1000;
    const recent = allRuns.filter((r) => r.created_at && new Date(r.created_at).getTime() >= since);
    const count = (tone) => recent.filter((r) => runState(r)[0] === tone).length;
    const truncated = allRuns.length >= HISTORY_LIMIT && recent.length === allRuns.length;
    return { total: recent.length, truncated, ful: count('ful'), hold: count('hold'), can: count('can') };
  }, [allRuns]);

  // Every system a run checks, from the newest run that recorded them —
  // the connector list lives on the server, so this reflects it exactly.
  const [serverSystems, setServerSystems] = useState([]);
  const [notifyAlways, setNotifyAlways] = useState([]);       // always emailed when the first steps finish
  const knownSystems = useMemo(() => {
    if (serverSystems.length) return serverSystems;            // what a new run would check right now (a newly connected system shows at once)
    const withSystems = allRuns.find((r) => isRealRun(r) && (r.systems || []).length > 1);
    return withSystems ? withSystems.systems.map((s) => s.system) : [];
  }, [allRuns, serverSystems]);

  const searching = historySearch.trim().length > 0;
  const baseRuns = searching ? (searchRuns || []) : allRuns;
  const filteredRuns = baseRuns.filter((r) => matchesFilter(r, statusFilter));
  const filterCounts = useMemo(() => Object.fromEntries(FILTERS.map(([k]) => [k, allRuns.filter((r) => matchesFilter(r, k)).length])), [allRuns]);
  const clearFilters = () => { setStatusFilter('all'); setHistorySearch(''); };
  const openRun = (run) => navigate(runPath(run));
  const pendingDeletion = deletionRows.filter((r) => !r.deleted_on);

  const statButton = (key, value, label) => (
    <button
      type="button"
      className="uh-stat"
      aria-pressed={key !== 'all' && statusFilter === key}
      onClick={() => { setStatusFilter(key === 'all' || statusFilter === key ? 'all' : key); setTab('history'); }}
    >
      <span className="v">{value}</span>
      <span className="k">{label}</span>
    </button>
  );

  const historyControls = (
    <>
      <span className="uh-sp" />
      <div className="uh-search">
        <Ic source={SearchIcon} sm />
        <input
          value={historySearch}
          onChange={(e) => setHistorySearch(e.target.value)}
          placeholder="Search by name or email"
          aria-label="Search runs"
        />
      </div>
      <select className="uh-select" aria-label="Status filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        {FILTERS.map(([k, l]) => (
          <option key={k} value={k}>{l}{k !== 'all' && filterCounts[k] ? ` (${filterCounts[k]})` : ''}</option>
        ))}
      </select>
    </>
  );

  const attentionCount = stats.hold + stats.can;
  const systemCount = knownSystems.length;
  const onStartTab = tab === 'single' || tab === 'csv';

  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Offboarding</h1>
          <div className="uh-sub">Remove access, archive data and forward communications for departing employees.</div>
        </div>
        <div className="uh-statcard">
          <div className={`state${attentionCount ? ' hold' : ''}`}>
            <span className="ic"><Ic source={attentionCount ? AlertTriangleIcon : CheckIcon} sm /></span>
            <div>
              <b>Offboarding status</b>
              <span className="line">
                {attentionCount
                  ? [stats.hold && `${stats.hold} need${stats.hold === 1 ? 's' : ''} attention`, stats.can && `${stats.can} failed`].filter(Boolean).join(' · ')
                  : 'Nothing needs attention'}
              </span>
            </div>
          </div>
          <div className="stats">
            {statButton('all', `${stats.total}${stats.truncated ? '+' : ''}`, `Total runs (${STATS_WINDOW_DAYS}d)`)}
            {statButton('ful', stats.ful, 'Success')}
            {statButton('hold', stats.hold, 'Needs attention')}
            {statButton('can', stats.can, 'Errors')}
          </div>
        </div>
      </div>

      <div className="uh-card">
        <div className="uh-tabs" role="tablist" aria-label="Offboarding">
          {[
            ['single', PersonIcon, 'Single user'],
            ['csv', FileIcon, 'CSV import'],
            ['history', ClockIcon, 'History'],
            ['delete', DeleteIcon, 'Delete'],
          ].map(([key, icon, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
              <Ic source={icon} sm />{label}
              {key === 'history' && allRuns.length > 0 && <span className="uh-tcount">{allRuns.length}{allRuns.length >= HISTORY_LIMIT ? '+' : ''}</span>}
              {key === 'delete' && pendingDeletion.length > 0 && <span className="uh-tcount warn">{pendingDeletion.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {onStartTab && (
        <>
          <div className="uh-start">
            <div className="uh-card uh-card-b uh-form-card">
              <div className="uh-note">
                <Ic source={InfoIcon} sm />
                <div>
                  {tab === 'single'
                    ? `The employee will be searched across ${systemCount ? `all ${systemCount}` : 'every'} connected system${systemCount === 1 ? '' : 's'}. Clean matches are removed or archived automatically; anything ambiguous waits for your approval. Nothing happens until you preview and type ${CONFIRM_PHRASE}.`
                    : `Every row runs the full multi-system offboarding, the same as a single user. Leave email blank for someone with no company email; they're searched by name everywhere. Nothing happens until you preview and type ${CONFIRM_PHRASE}.`}
                </div>
              </div>

              {tab === 'single' ? (
                <div className="uh-grid2">
                  <div className="uh-fld">
                    <label htmlFor="ob-name">Departing employee's full name <span className="req">*</span></label>
                    <input id="ob-name" className="uh-inp" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Jane Doe" autoComplete="off" />
                    <span className="uh-help">Seeds the search in every system. Nicknames or maiden names may not match.</span>
                  </div>
                  <div className="uh-fld">
                    <label htmlFor="ob-email">Email <span className="optl">(optional)</span></label>
                    <input id="ob-email" className="uh-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jdoe@soccerpost.com" autoComplete="off" />
                    <span className="uh-help">Blank if they have no company email. Workspace is then checked by name, and any match needs your approval.</span>
                  </div>
                  <div className="uh-fld">
                    <label htmlFor="ob-fwd">Forward mail to <span className="optl">(comma or semicolon separated, optional)</span></label>
                    <input id="ob-fwd" className="uh-inp" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} placeholder="manager@soccerpost.com, sales@soccerpost.com" autoComplete="off" />
                    <span className="uh-help">Blank = no rename or continuity group; the account is just suspended and archived.</span>
                  </div>
                  <div className="uh-fld">
                    <label htmlFor="ob-notify">Email when the first steps are done <span className="optl">(optional)</span></label>
                    <input id="ob-notify" className="uh-inp" type="email" value={notifyTo} onChange={(e) => setNotifyTo(e.target.value)} placeholder="manager@soccerpost.com" autoComplete="off" />
                    <span className="uh-help">They get a summary of what was done for this person. The mail archive keeps running in the background and can take hours to days.{notifyAlways.length > 0 && ` ${notifyAlways.join(', ')} always gets this email too.`}</span>
                  </div>
                  <div className="uh-fld">
                    <label htmlFor="ob-when">Schedule for later <span className="optl">(optional)</span></label>
                    <input id="ob-when" className="uh-inp" type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} />
                    <span className="uh-help">Blank runs now. A scheduled run searches and resolves matches now, then holds every change until this time.</span>
                  </div>
                </div>
              ) : (
                <div className="uh-fld">
                  <label htmlFor="ob-csv">Departures CSV</label>
                  <div className="uh-actions">
                    <input id="ob-csv" ref={csvInputRef} type="file" accept=".csv" hidden onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                    <button type="button" className="uh-btn" onClick={() => csvInputRef.current?.click()}>
                      <Ic source={UploadIcon} sm />{csvFile ? 'Choose a different file' : 'Choose CSV file'}
                    </button>
                    {csvFile ? <span className="uh-mono">{csvFile.name}</span> : <span className="uh-help">No file chosen</span>}
                  </div>
                  <span className="uh-help">
                    Header row with <code>email</code>, <code>full_name</code> and <code>forward_to</code> columns, any order.
                    forward_to is semicolon-separated. full_name is required whenever email is blank.
                  </span>
                </div>
              )}

              {tab === 'csv' && csvErrors.length > 0 && (
                <Alert>
                  <b>{csvErrors.length} row{csvErrors.length === 1 ? '' : 's'} skipped.</b>
                  {csvErrors.map((e, i) => <div key={i}>{e}</div>)}
                </Alert>
              )}
              {previewError && <Alert tone="crit">{previewError}</Alert>}

              <button
                type="button"
                className={`uh-btn primary wide${previewLoading ? ' busy' : ''}`}
                disabled={previewLoading || (tab === 'single' ? (!email.trim() && !fullName.trim()) : !csvFile)}
                onClick={tab === 'single' ? previewSingleUser : previewCsv}
              >
                <Ic source={ViewIcon} sm />{previewLoading ? 'Previewing…' : 'Preview'}
              </button>
            </div>

            <div className="uh-col">
              <div className="uh-card uh-card-b">
                <h2 className="uh-h2 lg">What happens next</h2>
                <ol className="uh-steps">
                  <li><span className="sn">1</span><div>
                    <b>Search for employee</b>
                    <span>Every connected system is checked for the employee's accounts and related data.</span>
                    {knownSystems.length > 0 && <div className="uh-syschips">{knownSystems.map((s) => <i key={s}>{systemLabel(s)}</i>)}</div>}
                  </div></li>
                  <li><span className="sn">2</span><div>
                    <b>Request approval</b>
                    <span>A close-but-not-exact match, or a step with no API such as Shopify staff, waits for you.</span>
                  </div></li>
                  <li><span className="sn">3</span><div>
                    <b>Suspend &amp; archive</b>
                    <span>Access is removed in each system. Workspace mail and Drive are archived to Terminated User Backups.</span>
                  </div></li>
                  <li><span className="sn">4</span><div>
                    <b>Forward communications</b>
                    <span>Mail is forwarded to the address(es) you provide through a continuity group.</span>
                  </div></li>
                </ol>
              </div>

            </div>
          </div>

          {previewVisible && (
            <div className="uh-card" ref={previewRef}>
              <div className="uh-card-h">
                <h2 className="uh-h2">Preview: Google Workspace</h2>
                <StatusPill tone="neu">Nothing has happened yet</StatusPill>
                <span className="uh-sp" />
                <span className="uh-help">Every other system is searched when the run starts.</span>
              </div>
              <PreviewTable rows={previewRows} />
              {(!previewRows.every(rowIsReadyToRun) || previewRows.some(r => r.already_offboarded)) && (
                <div className="uh-card-b uh-col" style={{ gap: 8 }}>
                  {!previewRows.every(r => (r.email ? r.exists : true)) && (
                    <Alert tone="crit">One or more accounts couldn't be found. Fix the address and preview again. Running is disabled until every row resolves.</Alert>
                  )}
                  {previewRows.some(r => r.already_offboarded) && (
                    <Alert>One or more accounts are already offboarded. Remove them before running, or double-check you meant to re-run this.</Alert>
                  )}
                </div>
              )}
              <div className="uh-confirm">
                <div className="uh-fld">
                  <label htmlFor="ob-confirm">Type {CONFIRM_PHRASE} to confirm</label>
                  <input id="ob-confirm" className="uh-inp" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={CONFIRM_PHRASE} autoComplete="off" disabled={running} />
                </div>
                <button
                  type="button"
                  className="uh-btn danger"
                  disabled={!canRun}
                  onClick={tab === 'single' ? startSingleUserFullRun : startCsvBatchRun}
                >
                  <Ic source={PlayIcon} sm />
                  {running ? 'Running…' : tab === 'single' ? (scheduledFor ? 'Schedule offboarding' : 'Run offboarding') : `Run ${previewRows.length} offboarding${previewRows.length === 1 ? '' : 's'}`}
                </button>
                {tab === 'single' && running && <button type="button" className="uh-btn" onClick={cancelRun}>Cancel</button>}
              </div>
            </div>
          )}

          {(fullRunError || (tab === 'single' && (runLog.length > 0 || runResult)) || (tab === 'csv' && csvBatchResult)) && (
            <div className="uh-card uh-card-b uh-col" style={{ gap: 10 }}>
              {fullRunError && <Alert tone="crit">{fullRunError}</Alert>}
              {tab === 'single' && (runLog.length > 0 || running) && (
                <pre className="uh-log" ref={logRef}>{runLog.join('\n')}</pre>
              )}
              {tab === 'single' && runResult && !fullRun && (
                runResult.exit_code === 0 ? (
                  <Alert tone="ok">
                    Phase 1 completed: suspended, renamed and moved to Pending-Archive. The mail backup keeps running in
                    the background and is <b>not yet verified</b>. To follow it on the server:
                    <pre className="uh-log" style={{ marginTop: 6 }}>{gybLogHint(pendingRows)}</pre>
                  </Alert>
                ) : (
                  <Alert tone="crit">Phase 1 exited with code {runResult.exit_code}. See the log above.</Alert>
                )
              )}
              {tab === 'csv' && csvBatchResult && (
                <Alert tone="ok">
                  {csvBatchResult.run_ids.length} run{csvBatchResult.run_ids.length === 1 ? '' : 's'} created. History is filtered to runs that need attention.
                </Alert>
              )}
            </div>
          )}

          {tab === 'single' && fullRun && (
            <>
              <div className="uh-actions">
                <h2 className="uh-h2 lg">This run: {fullRun.full_name || fullRun.email}</h2>
                <StatusPill tone={pillTone(runState(fullRun)[0])}>{runState(fullRun)[1]}</StatusPill>
                <span className="uh-sp" />
                <button type="button" className="uh-btn slim" onClick={() => navigate(runPath(fullRun))}>
                  Open run page<Ic source={ChevronRightIcon} sm />
                </button>
              </div>
              <SystemsTable key={fullRun.id} run={fullRun} onResolve={handleResolveAction} busyActionId={resolveBusyActionId} />
            </>
          )}

          <div className="uh-card">
            <div className="uh-card-h">
              <div>
                <h2 className="uh-h2 lg">History</h2>
                <div className="uh-sub">Recent offboarding runs and their status in every system.</div>
              </div>
              {historyControls}
            </div>
            <HistoryTable
              rows={filteredRuns}
              loading={historyLoading}
              error={historyError}
              limit={RECENT_ROWS}
              onOpen={openRun}
              onClear={clearFilters}
              filtered={searching || statusFilter !== 'all'}
            />
            <div className="uh-tfoot">
              <Legend />
              {filteredRuns.length > RECENT_ROWS && (
                <span className="uh-pager">
                  <button type="button" className="uh-link" onClick={() => setTab('history')}>View all {filteredRuns.length} runs</button>
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'history' && (
        <div className="uh-card">
          <div className="uh-card-h">
            <div>
              <h2 className="uh-h2 lg">History</h2>
              <div className="uh-sub">Every offboarding run. Click a row for each system's detail.</div>
            </div>
            {historyControls}
          </div>
          {csvBatchResult && (
            <div className="uh-card-b" style={{ paddingBottom: 0 }}>
              <Alert tone="ok">
                {csvBatchResult.run_ids.length} run{csvBatchResult.run_ids.length === 1 ? '' : 's'} created from the CSV. Showing runs that need attention.
              </Alert>
            </div>
          )}
          <HistoryTable
            rows={filteredRuns}
            loading={historyLoading}
            error={historyError}
            onOpen={openRun}
            onClear={clearFilters}
            filtered={searching || statusFilter !== 'all'}
          />
          <div className="uh-tfoot">
            <Legend />
            <span className="uh-pager uh-num">
              {filteredRuns.length} run{filteredRuns.length === 1 ? '' : 's'}
              {!searching && allRuns.length >= HISTORY_LIMIT ? ` (newest ${HISTORY_LIMIT}; search to find older runs)` : ''}
            </span>
          </div>
        </div>
      )}

      {tab === 'delete' && (
        <div className="uh-card">
          <div className="uh-card-h">
            <div>
              <h2 className="uh-h2 lg">Delete</h2>
              <div className="uh-sub">Google Workspace accounts whose Phase 2 archive already succeeded.</div>
            </div>
            <span className="uh-sp" />
            <button type="button" className="uh-btn slim" onClick={loadDeletionCandidates}>Refresh</button>
          </div>
          <div className="uh-card-b" style={{ paddingBottom: 0 }}>
            <Alert>
              Deleting an account is permanent and immediately frees its Workspace license. There is no Vault coverage on
              these accounts: whatever wasn't captured by Phase 2 or transferred here is gone for good. Transfer Drive
              ownership first, then delete.
            </Alert>
          </div>
          {deletionError && <div className="uh-card-b"><Alert tone="crit">{deletionError}</Alert></div>}
          <div className="uh-tscroll" style={{ marginTop: 14 }}>
            <table className="uh-tbl">
              <thead><tr><th>Original email</th><th>Archived as</th><th>Drive transfer</th><th className="r">Action</th></tr></thead>
              <tbody>
                {deletionLoading && !deletionRows.length ? (
                  <tr><td colSpan={4} className="uh-empty">Loading…</td></tr>
                ) : !pendingDeletion.length ? (
                  <tr><td colSpan={4} className="uh-empty">No accounts are waiting on deletion right now.</td></tr>
                ) : pendingDeletion.map((row) => {
                  const busy = !!transferBusy[row.archived_email] || !!deleteBusy[row.archived_email];
                  return (
                    <React.Fragment key={row.archived_email}>
                      <tr>
                        <td className="nm uh-mono">{row.original_email}</td>
                        <td className="uh-mono">{row.archived_email}</td>
                        <td><DeletionStatus status={row.transfer_status} /></td>
                        <td className="r">
                          {(row.transfer_status === 'not_started' || row.transfer_status === 'failed') && (
                            <button type="button" className="uh-btn slim" disabled={busy} onClick={() => startTransfer(row.archived_email)}>
                              <Ic source={PlayIcon} sm />
                              {transferBusy[row.archived_email] ? 'Starting…' : row.transfer_status === 'failed' ? 'Retry Drive transfer' : 'Start Drive transfer'}
                            </button>
                          )}
                          {ACTIVE_TRANSFER_STATUSES.includes(row.transfer_status) && (
                            <button type="button" className="uh-btn slim" disabled><Ic source={DeleteIcon} sm />Delete</button>
                          )}
                          {row.transfer_status === 'completed' && (
                            <button type="button" className="uh-btn danger slim" disabled={busy} onClick={() => openDeleteDialog(row)}>
                              <Ic source={DeleteIcon} sm />{deleteBusy[row.archived_email] ? 'Deleting…' : 'Delete…'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {rowErrors[row.archived_email] && (
                        <tr><td colSpan={4}><Alert tone="crit">{rowErrors[row.archived_email]}</Alert></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <dialog className="uh-dlg" ref={deleteDialogRef} aria-labelledby="ob-del-h" onCancel={() => setDeleteTarget(null)}>
        <div className="uh-dlg-h">
          <h2 id="ob-del-h">Delete account permanently?</h2>
          <button type="button" className="uh-ibtn" aria-label="Close" onClick={closeDeleteDialog}><Ic source={XSmallIcon} /></button>
        </div>
        <div className="uh-dlg-b">
          <p style={{ margin: 0 }}>
            This permanently deletes <b className="uh-mono">{deleteTarget?.archived_email}</b> and frees its Workspace
            license. Its Drive files have already been transferred and its mail archive stays in Terminated User Backups.
          </p>
          <div className="uh-fld">
            <label htmlFor="ob-del-confirm">Type {DELETE_CONFIRM_PHRASE} to confirm</label>
            <input
              id="ob-del-confirm"
              className="uh-inp"
              autoComplete="off"
              placeholder={DELETE_CONFIRM_PHRASE}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
          </div>
        </div>
        <div className="uh-dlg-f">
          <button type="button" className="uh-btn" onClick={closeDeleteDialog}>Cancel</button>
          <button type="button" className="uh-btn danger" disabled={deleteConfirmText !== DELETE_CONFIRM_PHRASE} onClick={confirmDelete}>
            <Ic source={DeleteIcon} sm />Delete account
          </button>
        </div>
      </dialog>
    </div>
  );
}

export default Offboarding;
